/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Brightness & Contrast Node
   Standard professional photographic tone control on GPU.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const BRIGHTNESS_CONTRAST_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform float u_brightness; // -1.0 .. 1.0
uniform float u_contrast;   // -1.0 .. 1.0

void main() {
  vec4 src = texture(u_image, v_uv);
  
  // 1. Apply brightness
  vec3 rgb = src.rgb + vec3(u_brightness);
  
  // 2. Apply contrast around midpoint 0.5
  float contrastFactor = (1.0 + u_contrast);
  rgb = (rgb - vec3(0.5)) * max(0.0, contrastFactor) + vec3(0.5);
  
  fragColor = vec4(clamp(rgb, 0.0, 1.0), src.a);
}
`;

export const brightnessContrastNode: NodeDefinition = {
  type: 'color.brightness_contrast',
  category: 'Color',
  label: 'Brightness / Contrast',
  description: 'Adjusts overall image exposure and contrast ratio on GPU',
  keywords: ['brightness', 'contrast', 'exposure', 'tone', 'color', 'adjust'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'brightness',
      name: 'Brightness',
      type: 'slider',
      defaultValue: 0,
      min: -100,
      max: 100,
      step: 1,
      unit: '%',
    },
    {
      id: 'contrast',
      name: 'Contrast',
      type: 'slider',
      defaultValue: 0,
      min: -100,
      max: 100,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: BRIGHTNESS_CONTRAST_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_brightness: (Number(params.brightness) || 0) / 100,
      u_contrast: (Number(params.contrast) || 0) / 100,
    }),
  }),
};
